import { img } from "../images";
import type { Service, ServiceCategory } from "./types";

export const aestheticCategory: ServiceCategory = {
  slug: "aesthetic-dermatology",
  name: "Aesthetic Dermatology",
  short: "Refined cosmetic skin care, done properly.",
  tagline: "Cosmetic skin care, performed safely by a dermatologist.",
  image: img.aesthetic,
  doctorSlug: "dr-m-vanitha",
  intro:
    "Aesthetic dermatology at AV Clinic focuses on improving the appearance and health of your skin through professionally evaluated cosmetic treatments such as chemical peels, skin rejuvenation and pigmentation and scar care.",
  description:
    "Aesthetic dermatology sits at the meeting point of medicine and skin care. It is about improving the appearance of your skin — tone, texture, brightness and the look of scars or pigmentation — using methods that are medically evaluated and appropriate for your skin type. At AV Clinic, cosmetic care is never a one-size-fits-all routine. Each treatment begins with a consultation that assesses your skin, your goals and your medical history, so that the approach chosen is safe and suited to you. From a refreshing chemical peel to a plan for stubborn pigmentation or scars, the aim is natural-looking, gradual improvement — with clear advice on aftercare, sun protection and what to expect along the way.",
  conditions: [
    "chemical-peels",
    "skin-rejuvenation",
    "pigmentation-peels",
    "scar-treatment",
    "cosmetic-dermatology",
  ],
  concerns: [
    "Skin rejuvenation and glow",
    "Pigmentation and uneven tone",
    "Acne scars and other scars",
    "Dull or tired-looking skin",
    "Signs of skin ageing",
  ],
  management: [
    "Personalised cosmetic dermatology consultation",
    "Chemical peels suited to your skin concerns",
    "Pigmentation and scar care plans",
    "Skin care guidance for long-term results",
    "Safe, medically supervised aesthetic care",
  ],
  faqs: [
    {
      q: "Are chemical peels safe for all skin types?",
      a: "Peels are selected based on your skin type, concerns and sensitivity. Your dermatologist will recommend the most appropriate peel and aftercare to minimise risk and optimise results.",
    },
    {
      q: "How is aesthetic dermatology different from a spa?",
      a: "It is performed by a qualified dermatologist using medical-grade methods, with a focus on safety, appropriateness and realistic results for your skin.",
    },
    {
      q: "How many sessions will I need?",
      a: "This depends on your concern and goals. A plan is outlined during consultation, and results are typically gradual.",
    },
  ],
  related: ["chemical-peels", "skin-rejuvenation", "pigmentation", "acne-scars"],
  diagnosisSteps: [
    { title: "Skin Consultation", desc: "Your skin type, concerns and goals are discussed." },
    { title: "Skin Assessment", desc: "Tone, texture, sensitivity and concerns are examined." },
    { title: "Treatment Selection", desc: "The safest, most suitable option is chosen for you." },
    { title: "Aftercare Plan", desc: "Clear aftercare and sun-protection guidance is provided." },
  ],
  specialistApproach: [
    { icon: "ri-headphone-line", title: "Listen", desc: "Understand your skin goals and expectations." },
    { icon: "ri-search-eye-line", title: "Assess", desc: "Review your skin type, tone and sensitivity." },
    { icon: "ri-stethoscope-line", title: "Diagnose", desc: "Identify the concern and the right approach." },
    { icon: "ri-calendar-check-line", title: "Plan", desc: "Design a safe, gradual treatment and aftercare plan." },
  ],
  patientEducation: [
    { icon: "ri-list-check-2", title: "Before Treatment", desc: "Tell us about any skin conditions, medications and past reactions." },
    { icon: "ri-image-2-line", title: "Know Your Skin", desc: "Be clear about the look you want and any concerns about downtime." },
    { icon: "ri-question-line", title: "Questions to Ask", desc: "Ask about sessions needed, expected results, cost and aftercare." },
    { icon: "ri-refresh-line", title: "Protect & Maintain", desc: "Sun protection and consistent skin care protect your results." },
  ],
  myths: [
    { myth: "Stronger peels give faster results.", fact: "Peels are selected for your skin type. The right strength for you is safer and more effective than the strongest." },
    { myth: "Aesthetic treatments can completely change skin overnight.", fact: "Results are gradual and natural-looking; consistency and aftercare are key." },
  ],
  seo: {
    title: "Aesthetic Dermatology in Krishnagiri | Chemical Peels & Skin Care | AV Clinic",
    description:
      "Aesthetic dermatology in Krishnagiri — chemical peels, skin rejuvenation, pigmentation and scar care performed safely by a dermatologist at AV Clinic. Book a consultation.",
  },
};

export const aestheticServices: Service[] = [
  {
    slug: "chemical-peels",
    name: "Chemical Peels",
    category: "Aesthetic Dermatology",
    categorySlug: "aesthetic-dermatology",
    blurb: "Medically supervised peels to refresh and renew your skin.",
    tagline: "Fresh, Renewed Skin — Safely.",
    intro:
      "Chemical peels use controlled solutions to exfoliate and renew the skin. Performed by a dermatologist, they help improve tone, texture and brightness, with the peel strength matched to your skin.",
    image: img.serviceSkinCare,
    symptoms: [
      { name: "Dull skin", desc: "A tired, lacklustre complexion." },
      { name: "Uneven tone", desc: "Patchy colour and textural irregularity." },
      { name: "Mild pigmentation", desc: "Sun spots and faint dark patches." },
      { name: "Textured skin", desc: "Roughness and enlarged-looking pores." },
    ],
    causes: [
      "Sun exposure and ageing",
      "Uneven pigment production",
      "Build-up of dead skin cells",
      "Past acne or inflammation",
    ],
    whenToSee: [
      "You want brighter, more even skin",
      "You have pigmentation or texture concerns",
      "You want a professionally supervised approach",
    ],
    diagnosisSteps: [
      { title: "Skin-Type Assessment", desc: "Your skin type and sensitivity are determined." },
      { title: "Concern Review", desc: "Your specific goals are discussed." },
      { title: "Peel Selection", desc: "The appropriate peel strength is chosen." },
      { title: "Aftercare Plan", desc: "Post-peel care and sun protection are explained." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Chemical peels selected for your skin type",
        "A series of sessions as appropriate",
      ],
      lifestyle: [
        "Strict sun protection after peels",
        "Gentle skin care and moisturising",
      ],
      followUp:
        "Results improve gradually over a series of sessions. Your skin is reviewed and the plan adjusted as needed.",
    },
    myths: [
      { myth: "Are chemical peels painful?", fact: "Most peels cause a mild tingling sensation. Your dermatologist will choose a peel strength suited to your skin and comfort." },
      { myth: "Peels thin the skin over time.", fact: "When performed and spaced appropriately, peels renew the surface without thinning healthy skin." },
    ],
    prevention: [
      "Use daily sun protection",
      "Maintain a gentle skin-care routine",
      "Follow post-peel aftercare",
    ],
    management: [
      "Skin-type assessment",
      "Selection of the appropriate peel",
      "Safe application and aftercare",
      "Gradual, natural-looking improvement",
    ],
    faqs: [
      { q: "Are chemical peels painful?", a: "Most peels cause a mild tingling sensation. Your dermatologist will choose a peel strength suited to your skin and comfort." },
      { q: "How many peels do I need?", a: "This depends on your goal. Many people have a series of peels spaced a few weeks apart for best results." },
      { q: "How long is the downtime?", a: "Mild flaking or redness is common for a few days. Your dermatologist will explain what to expect for your skin." },
      { q: "Can anyone have a chemical peel?", a: "Peels are tailored to your skin type. A consultation determines whether a peel is right for you and which strength." },
    ],
    related: ["skin-rejuvenation", "pigmentation-peels", "pigmentation", "acne-scars"],
    relatedBlogs: ["why-is-my-skin-pigmented"],
    seo: {
      title: "Chemical Peels in Krishnagiri | Dermatologist | AV Clinic",
      description:
        "Chemical peels in Krishnagiri — medically supervised skin renewal to improve tone, texture and brightness. Safe aesthetic care by a dermatologist at AV Clinic.",
    },
  },
  {
    slug: "skin-rejuvenation",
    name: "Skin Rejuvenation",
    category: "Aesthetic Dermatology",
    categorySlug: "aesthetic-dermatology",
    blurb: "Restoring a fresh, healthy, even-looking complexion.",
    tagline: "Restore Your Skin's Natural Glow.",
    intro:
      "Skin rejuvenation combines professional treatments and skin care to restore radiance, improve tone and reduce the signs of tiredness and ageing. It is tailored to your skin and goals.",
    image: img.serviceSkinCare,
    symptoms: [
      { name: "Dull complexion", desc: "Skin that looks tired or lacks glow." },
      { name: "Uneven tone", desc: "Patchy colour and dullness." },
      { name: "Fine signs of ageing", desc: "Early lines and loss of freshness." },
    ],
    causes: [
      "Sun exposure",
      "Ageing",
      "Stress and lifestyle",
      "Inconsistent skin care",
    ],
    whenToSee: [
      "You want brighter, more even skin",
      "Skin looks dull despite your routine",
      "You want a professional plan",
    ],
    diagnosisSteps: [
      { title: "Skin Consultation", desc: "Your concerns and goals are discussed." },
      { title: "Assessment", desc: "Skin tone, texture and needs are examined." },
      { title: "Treatment Combination", desc: "Suitable treatments and care are combined." },
      { title: "Home-Care Plan", desc: "A maintenance routine is agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Chemical peels and renewing treatments",
        "Targeted care for tone and texture",
      ],
      lifestyle: [
        "Daily sun protection",
        "Consistent moisturising and gentle care",
      ],
      followUp:
        "A series of sessions is usually recommended. Reviews adjust the plan and maintain results.",
    },
    myths: [
      { myth: "Rejuvenation is only for older skin.", fact: "Skin rejuvenation can benefit any age by improving tone, texture and radiance." },
    ],
    prevention: [
      "Daily sunscreen",
      "Good sleep and hydration",
      "A consistent, gentle routine",
    ],
    management: [
      "Skin assessment and goals",
      "Combination of suitable treatments",
      "Home-care and protection advice",
    ],
    faqs: [
      { q: "How many sessions are needed?", a: "This depends on your skin and goals. Your dermatologist will outline a realistic plan during consultation." },
      { q: "Will I need downtime?", a: "Mild redness or flaking may occur after some treatments. Your dermatologist will explain what to expect." },
    ],
    related: ["chemical-peels", "pigmentation-peels", "pigmentation"],
    relatedBlogs: ["why-is-my-skin-pigmented"],
    seo: {
      title: "Skin Rejuvenation in Krishnagiri | Aesthetic Care | AV Clinic",
      description:
        "Skin rejuvenation in Krishnagiri — restore radiance, even tone and freshness with professional aesthetic dermatology care at AV Clinic.",
    },
  },
  {
    slug: "pigmentation-peels",
    name: "Pigmentation Peels",
    category: "Aesthetic Dermatology",
    categorySlug: "aesthetic-dermatology",
    blurb: "Targeted peels to help lighten stubborn dark patches.",
    tagline: "Lightening Stubborn Pigmentation, Carefully.",
    intro:
      "Pigmentation peels are specialised treatments designed to reduce dark patches and even out skin tone, often used for melasma and post-inflammatory darkening — selected carefully for Indian skin.",
    image: img.serviceSkinCare,
    symptoms: [
      { name: "Melasma", desc: "Facial brown patches, often hormonal." },
      { name: "Sun spots", desc: "Flat, sun-induced dark spots." },
      { name: "Uneven tone", desc: "Areas of darkening across the face." },
    ],
    causes: [
      "Sun exposure",
      "Hormonal changes",
      "Post-inflammation darkening",
      "Genetics",
    ],
    whenToSee: [
      "Dark patches are persistent",
      "Pigmentation is not responding to skin care",
      "You want a targeted, supervised approach",
    ],
    diagnosisSteps: [
      { title: "Pigmentation Assessment", desc: "The type and depth of pigmentation are reviewed." },
      { title: "Skin-Type Check", desc: "Skin sensitivity is considered to choose a safe peel." },
      { title: "Peel Selection", desc: "A suitable peel is chosen for your skin." },
      { title: "Sun-Protection Plan", desc: "Strict aftercare and sun protection are agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Pigmentation-targeted peels",
        "A series spaced appropriately",
      ],
      lifestyle: [
        "Daily broad-spectrum sunscreen",
        "Gentle skin care",
      ],
      followUp:
        "Pigmentation lightens gradually. Reviews adjust the treatment and help maintain the improvement.",
    },
    myths: [
      { myth: "Peels work overnight on pigmentation.", fact: "Pigmentation lightens gradually over sessions with consistent sun protection." },
      { myth: "Pigmentation peels are unsafe for Indian skin.", fact: "When selected and performed carefully for your skin type, with sun protection, they are safe and effective." },
    ],
    prevention: [
      "Daily sun protection",
      "Avoid heat and harsh products",
      "Follow the full aftercare plan",
    ],
    management: [
      "Assessment of pigmentation type",
      "Selection of a suitable peel",
      "Sun-protection guidance",
      "Realistic improvement expectations",
    ],
    faqs: [
      { q: "Are pigmentation peels safe for Indian skin?", a: "Yes, when performed and selected carefully by a dermatologist for your skin type, with appropriate sun protection." },
      { q: "How long before I see improvement?", a: "Most people notice gradual lightening over several sessions and weeks." },
      { q: "Will pigmentation return?", a: "Without sun protection, pigmentation can return. Consistent protection is essential to maintain results." },
    ],
    related: ["melasma", "pigmentation", "chemical-peels"],
    relatedBlogs: ["why-is-my-skin-pigmented"],
    seo: {
      title: "Pigmentation Peels in Krishnagiri | Melasma Care | AV Clinic",
      description:
        "Pigmentation peels in Krishnagiri — targeted care to lighten melasma and stubborn dark patches, selected safely for your skin at AV Clinic.",
    },
  },
  {
    slug: "scar-treatment",
    name: "Scar Treatment",
    category: "Aesthetic Dermatology",
    categorySlug: "aesthetic-dermatology",
    blurb: "Improving the appearance of scars from acne and other causes.",
    tagline: "Improving the Look of Scars.",
    intro:
      "Scar treatment aims to reduce the visibility of acne and other scars by improving texture and tone through dermatologically guided methods, with realistic expectations.",
    image: img.serviceSkinCare,
    symptoms: [
      { name: "Acne scars", desc: "Depressed or pitted marks after acne." },
      { name: "Depressed scars", desc: "Indented scars from injury or surgery." },
      { name: "Post-surgical marks", desc: "Scars from procedures." },
    ],
    causes: [
      "Deep acne inflammation",
      "Picking or squeezing",
      "Injury or surgery",
    ],
    whenToSee: [
      "Scars affect your confidence",
      "You want to improve their appearance",
      "You want a safe, professional plan",
    ],
    diagnosisSteps: [
      { title: "Scar-Type Assessment", desc: "The type, depth and extent of scars are evaluated." },
      { title: "Skin Review", desc: "Skin type and pigmentation are considered." },
      { title: "Treatment Selection", desc: "Suitable options are matched to your scars." },
      { title: "Realistic Plan", desc: "A gradual improvement plan is agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Resurfacing techniques where appropriate",
        "Chemical peels suited to your skin",
      ],
      lifestyle: [
        "Sun protection to prevent darkening",
        "Gentle skin care",
      ],
      followUp:
        "Scar improvement is gradual and often requires several sessions. Progress is reviewed and adjusted over time.",
    },
    myths: [
      { myth: "All scars can be completely removed.", fact: "Most scars can be significantly improved, but complete removal is not guaranteed." },
    ],
    prevention: [
      "Protect healing skin from the sun",
      "Avoid picking at scars",
    ],
    management: [
      "Scar-type assessment",
      "Suitable resurfacing and care options",
      "Realistic improvement goals",
    ],
    faqs: [
      { q: "Can all scars be improved?", a: "Most scars can be improved to some degree. The approach depends on the type, age and depth of the scar." },
      { q: "How many sessions are needed?", a: "This varies by scar type. A plan is outlined during consultation and adjusted as you respond." },
    ],
    related: ["acne-scars", "chemical-peels"],
    relatedBlogs: ["understanding-acne-scars"],
    seo: {
      title: "Scar Treatment in Krishnagiri | Dermatologist | AV Clinic",
      description:
        "Scar treatment in Krishnagiri — improve the appearance of acne and other scars with dermatology care and chemical peels at AV Clinic.",
    },
  },
  {
    slug: "cosmetic-dermatology",
    name: "Cosmetic Dermatology",
    category: "Aesthetic Dermatology",
    categorySlug: "aesthetic-dermatology",
    blurb: "Professional, medically supervised aesthetic skin care.",
    tagline: "Medically Supervised Cosmetic Skin Care.",
    intro:
      "Cosmetic dermatology offers medically supervised procedures and skin care to improve the appearance and health of your skin, safely and appropriately, with realistic expectations.",
    image: img.serviceSkinCare,
    symptoms: [
      { name: "Aesthetic skin concerns", desc: "A desire to refresh or improve your appearance." },
      { name: "Tone and texture", desc: "Concerns about evenness and smoothness." },
    ],
    causes: [
      "Sun exposure and ageing",
      "Skin history and genetics",
    ],
    whenToSee: [
      "You want safe, professional aesthetic care",
      "You want a plan suited to your skin",
    ],
    diagnosisSteps: [
      { title: "Consultation", desc: "Your goals and concerns are discussed." },
      { title: "Skin Assessment", desc: "Your skin is examined and needs determined." },
      { title: "Tailored Plan", desc: "Suitable procedures and care are planned." },
      { title: "Aftercare", desc: "Clear guidance is given for safety and results." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Medically supervised cosmetic procedures",
        "Chemical peels and renewing care",
      ],
      lifestyle: [
        "Sun protection",
        "Consistent skin care",
      ],
      followUp:
        "Depending on the treatment, follow-up sessions and reviews are planned for gradual, maintained results.",
    },
    myths: [
      { myth: "Cosmetic dermatology is the same as a beauty spa.", fact: "It is performed by a qualified dermatologist using medical-grade methods, prioritising safety and suitability." },
    ],
    management: [
      "Consultation and skin assessment",
      "Tailored cosmetic care plan",
      "Safe, professional procedures",
    ],
    faqs: [
      { q: "How is cosmetic dermatology different from a spa?", a: "It is performed by a qualified dermatologist using medical-grade methods, with a focus on safety and appropriateness for your skin." },
      { q: "Are cosmetic treatments painful?", a: "Most are well tolerated. Your dermatologist will explain what to expect and how to manage any discomfort." },
    ],
    related: ["skin-rejuvenation", "chemical-peels", "pigmentation"],
    relatedBlogs: ["why-see-a-dermatologist-in-krishnagiri"],
    seo: {
      title: "Cosmetic Dermatology in Krishnagiri | AV Clinic",
      description:
        "Cosmetic dermatology in Krishnagiri — safe, medically supervised aesthetic skin care by a dermatologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "laser-hair-reduction",
    name: "Laser Hair Reduction",
    category: "Aesthetic Dermatology",
    categorySlug: "aesthetic-dermatology",
    blurb: "A safe, effective option for long-lasting reduction of unwanted hair.",
    tagline: "Smooth, Carefree Skin — Safely.",
    intro:
      "Laser hair reduction uses controlled light energy to target the hair follicle, reducing the amount and density of unwanted hair over a series of sessions. It is performed by a qualified dermatologist and tailored to your skin and hair type for safe, comfortable results.",
    image: img.svcLaserHairReduction,
    symptoms: [
      { name: "Unwanted facial hair", desc: "Hair on the upper lip, chin, cheeks or jawline." },
      { name: "Unwanted body hair", desc: "Hair on the legs, arms, underarms or bikini line." },
      { name: "Recurring hair growth", desc: "Hair that grows back quickly after shaving or waxing." },
      { name: "Ingrown hairs", desc: "Should you have ingrown or irritated follicles." },
    ],
    causes: [
      "Normal hormonal hair growth",
      "Hormonal changes or imbalances",
      "Genetics and skin type",
      "Recurrent shaving or waxing irritation",
    ],
    whenToSee: [
      "You are tired of shaving, waxing or plucking",
      "You want a long-lasting reduction in unwanted hair",
      "You have sensitive skin or recurrent ingrown hairs",
      "You want a professional, supervised approach",
    ],
    diagnosisSteps: [
      { title: "Skin & Hair Assessment", desc: "Your skin type and hair colour are reviewed." },
      { title: "Treatment Selection", desc: "The most suitable laser setting is chosen for you." },
      { title: "Patch Test", desc: "A small test may be done to confirm suitability and comfort." },
      { title: "Session Plan", desc: "A series of sessions and intervals are planned." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Laser sessions tailored to your skin and hair type",
        "A planned series of sessions for best results",
      ],
      lifestyle: [
        "Avoiding sun exposure before and after sessions",
        "Following aftercare guidance provided",
      ],
      followUp:
        "Hair is reduced gradually over a series of sessions. Follow-up sessions and aftercare guidance help maintain the result.",
    },
    myths: [
      { myth: "Laser hair removal is permanent.", fact: "Laser hair reduction greatly reduces hair, but some may regrow over time. It is a reduction, not always a complete permanent removal." },
      { myth: "Laser hair removal is painful.", fact: "Most people experience a mild, brief sensation. Settings are adjusted for comfort and your skin type." },
    ],
    prevention: [
      "Use sun protection between sessions",
      "Avoid plucking or waxing between sessions",
      "Follow aftercare advice",
    ],
    management: [
      "Assessment of skin and hair type",
      "Appropriate laser settings for comfort",
      "A planned series of sessions",
      "Aftercare and sun-protection guidance",
    ],
    faqs: [
      { q: "Is laser hair removal permanent?", a: "It provides a long-lasting reduction in hair, though some regrowth can occur over time. A dermatologist will explain what to expect." },
      { q: "Is it suitable for all skin types?", a: "Laser can be used on many skin and hair types when settings are adjusted appropriately. Suitability is confirmed during assessment." },
      { q: "How many sessions will I need?", a: "Most people need a series of sessions spaced a few weeks apart, as hair grows in cycles." },
      { q: "Is laser hair reduction painful?", a: "Most experience a mild, brief sensation. Settings are tailored for comfort." },
    ],
    related: ["skin-rejuvenation", "cosmetic-dermatology", "advanced-skin-procedures"],
    relatedBlogs: ["why-see-a-dermatologist-in-krishnagiri"],
    seo: {
      title: "Laser Hair Reduction in Krishnagiri | Aesthetic Dermatology | AV Clinic",
      description:
        "Laser hair reduction in Krishnagiri — long-lasting reduction of unwanted hair, performed safely by a dermatologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "skin-brightening",
    name: "Skin Brightening",
    category: "Aesthetic Dermatology",
    categorySlug: "aesthetic-dermatology",
    blurb: "Improving the tone and radiance of dull, uneven skin.",
    tagline: "Brighter, More Even, Radiant Skin.",
    intro:
      "Skin brightening focuses on improving the tone, radiance and evenness of dull or uneven skin through professionally evaluated treatments and a consistent skin-care routine, with realistic, gradual results.",
    image: img.svcSkinBrightening,
    symptoms: [
      { name: "Dull skin", desc: "A tired, lacklustre complexion." },
      { name: "Uneven tone", desc: "Patchy areas of darker or lighter skin." },
      { name: "Dark spots", desc: "Freckles, sun spots or post-acne marks." },
      { name: "Rough texture", desc: "A slightly rough or uneven surface." },
    ],
    causes: [
      "Sun exposure",
      "Uneven pigment production",
      "Ageing and dryness",
      "Past inflammation or acne",
    ],
    whenToSee: [
      "You want brighter, more even skin",
      "You have dark spots that bother you",
      "Self-care is not improving the look of your skin",
    ],
    diagnosisSteps: [
      { title: "Skin Consultation", desc: "Your skin tone and concerns are reviewed." },
      { title: "Tone Assessment", desc: "The cause of dullness or uneven tone is examined." },
      { title: "Treatment Selection", desc: "Suitable treatments and care are chosen." },
      { title: "Home-Care Plan", desc: "A brightening routine and sun protection are agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Chemical peels to renew and brighten",
        "Targeted care for uneven tone",
      ],
      lifestyle: [
        "Daily broad-spectrum sunscreen",
        "Gentle, consistent skin care",
      ],
      followUp:
        "Brightening is gradual. A series of sessions plus consistent sun protection and skin care gives the best results.",
    },
    myths: [
      { myth: "Skin brightening whitens the skin.", fact: "Brightening improves tone, radiance and evenness. It does not change your natural skin colour." },
      { myth: "One treatment gives instant brightness.", fact: "Results are gradual and natural-looking, with consistency and sun protection being key." },
    ],
    prevention: [
      "Use sunscreen daily",
      "Maintain a gentle brightening routine",
      "Protect skin from repeated sun exposure",
    ],
    management: [
      "Assessment of skin tone and concerns",
      "Suitable brightening treatments",
      "Sun protection and skin-care guidance",
    ],
    faqs: [
      { q: "Does skin brightening change my skin colour?", a: "No. Brightening improves tone, radiance and evenness while preserving your natural skin colour." },
      { q: "How quickly will I see results?", a: "Brightening is gradual. Most people notice improvement over several weeks with consistent care and sun protection." },
      { q: "Are brightening treatments safe?", a: "When selected and performed by a dermatologist for your skin type, they are safe and effective." },
    ],
    related: ["chemical-peels", "pigmentation-peels", "skin-rejuvenation"],
    relatedBlogs: ["why-is-my-skin-pigmented"],
    seo: {
      title: "Skin Brightening in Krishnagiri | Aesthetic Care | AV Clinic",
      description:
        "Skin brightening in Krishnagiri — improve dull, uneven skin and dark spots with safe, professional aesthetic dermatology care at AV Clinic.",
    },
  },
  {
    slug: "anti-ageing",
    name: "Anti-Ageing Treatments",
    category: "Aesthetic Dermatology",
    categorySlug: "aesthetic-dermatology",
    blurb: "Professional care to refresh the look of ageing skin.",
    tagline: "Age Well, Look Fresh.",
    intro:
      "Anti-ageing treatments combine renewing procedures and a protective skin-care plan to refresh the appearance of ageing skin — improving firmness, brightness and texture — with realistic expectations and a natural-looking outcome.",
    image: img.svcAntiAgeing,
    symptoms: [
      { name: "Fine lines", desc: "Early, fine lines around the eyes and mouth." },
      { name: "Loss of firmness", desc: "Skin that feels less firm or toned over time." },
      { name: "Dullness", desc: "A tired, less radiant complexion." },
      { name: "Sun damage", desc: "Dark spots and roughness from sun exposure." },
    ],
    causes: [
      "Ageing over time",
      "Sun exposure",
      "Loss of collagen and hydration",
      "Lifestyle factors such as smoking and poor sleep",
    ],
    whenToSee: [
      "You want to refresh the look of ageing skin",
      "You want a safe, professional plan",
      "You want natural-looking results",
    ],
    diagnosisSteps: [
      { title: "Skin Assessment", desc: "Your skin and signs of ageing are reviewed." },
      { title: "Goal Setting", desc: "What you want to achieve is discussed." },
      { title: "Treatment Selection", desc: "Suitable renewing procedures are chosen." },
      { title: "Protective Plan", desc: "Long-term skin protection is agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Chemical peels and renewing treatments",
        "Targeted care for tone and firmness",
      ],
      lifestyle: [
        "Daily sunscreen",
        "Hydration, sleep and a balanced diet",
        "Avoiding smoking",
      ],
      followUp:
        "Results are gradual. A combination of treatments and a consistent protective routine gives the best, natural-looking outcome.",
    },
    myths: [
      { myth: "Anti-ageing treatments make skin look 'done'.", fact: "When performed appropriately, the aim is a refreshed, natural-looking result — not a dramatic change." },
      { myth: "Anti-ageing is only for older people.", fact: "Preventive care and freshening treatments can benefit any age." },
    ],
    prevention: [
      "Use daily sunscreen",
      "Protect skin from sun damage",
      "Maintain a healthy lifestyle",
    ],
    management: [
      "Assessment of skin and signs of ageing",
      "Suitable renewing treatments",
      "Protective skin-care and lifestyle advice",
    ],
    faqs: [
      { q: "Are anti-ageing treatments safe?", a: "Yes, when performed by a dermatologist and suited to your skin. Your dermatologist will explain expected results and aftercare." },
      { q: "How many sessions are needed?", a: "This depends on your goals and skin. A plan is outlined during consultation and adjusted as you respond." },
      { q: "Will results look natural?", a: "Yes — the aim is a refreshed, natural-looking improvement, not a dramatic change." },
    ],
    related: ["skin-rejuvenation", "chemical-peels", "cosmetic-dermatology"],
    relatedBlogs: ["why-see-a-dermatologist-in-krishnagiri"],
    seo: {
      title: "Anti-Ageing Treatments in Krishnagiri | Aesthetic Dermatology | AV Clinic",
      description:
        "Anti-ageing skin treatments in Krishnagiri — refresh the look of ageing skin with professional, natural-looking aesthetic dermatology care at AV Clinic.",
    },
  },
  {
    slug: "hair-growth-treatments",
    name: "Hair Growth Treatments",
    category: "Aesthetic Dermatology",
    categorySlug: "aesthetic-dermatology",
    blurb: "Targeted professional support for hair thinning and loss.",
    tagline: "Supporting Healthier, Fuller Hair.",
    intro:
      "Hair growth treatments combine dermatology-guided options, scalp care and a consistent plan to support hair thinning and loss. The right approach depends on the cause, so treatment begins with a thorough evaluation.",
    image: img.svcHairGrowth,
    symptoms: [
      { name: "Thinning hair", desc: "Overall reduced hair density." },
      { name: "Receding hairline", desc: "A hairline that moves back over time." },
      { name: "Patchy loss", desc: "Localised thinning or bald patches." },
      { name: "Increased shedding", desc: "More hair falling than usual." },
    ],
    causes: [
      "Pattern hair loss (genetics)",
      "Hormonal changes",
      "Stress and nutritional factors",
      "Scalp conditions",
    ],
    whenToSee: [
      "Hair thinning is noticeable",
      "You want a professional plan",
      "Shedding is excessive or persistent",
    ],
    diagnosisSteps: [
      { title: "Scalp & Hair Assessment", desc: "The pattern and density of hair are examined." },
      { title: "Cause Review", desc: "Genetics, stress, nutrition and hormones are discussed." },
      { title: "Treatment Selection", desc: "Options suited to your type of hair loss are chosen." },
      { title: "Consistent Plan", desc: "A realistic, sustainable plan is agreed." },
    ],
    treatment: {
      medical: [
        "Dermatology-guided hair growth treatment",
        "Scalp care matched to your condition",
      ],
      procedures: [],
      lifestyle: [
        "Balanced nutrition",
        "Gentle hair handling",
        "Managing stress",
      ],
      followUp:
        "Hair grows slowly, so results take months. Regular review tracks progress and adjusts the plan.",
    },
    myths: [
      { myth: "Hair growth treatments work overnight.", fact: "Hair grows slowly, so visible improvement takes several months of consistent care and follow-up." },
    ],
    prevention: [
      "Maintain a balanced diet",
      "Handle hair gently",
      "Protect hair from heat and chemical stress",
    ],
    management: [
      "Evaluation of hair and scalp",
      "Treatment suited to the cause",
      "Scalp care and lifestyle guidance",
      "Monitoring of progress",
    ],
    faqs: [
      { q: "How long before hair growth treatments show results?", a: "Hair grows slowly, so most treatments take several months of consistent use before improvement is visible." },
      { q: "What causes hair thinning?", a: "Common causes include genetics, hormonal changes, stress, nutrition and scalp conditions. A specialist can identify the cause for you." },
      { q: "Are hair growth treatments safe?", a: "When recommended and supervised by a dermatologist for your situation, they are safe and appropriate." },
    ],
    related: ["hair-loss", "scalp-disorders", "cosmetic-dermatology"],
    relatedBlogs: ["hair-fall-common-causes"],
    seo: {
      title: "Hair Growth Treatments in Krishnagiri | AV Clinic",
      description:
        "Hair growth treatments in Krishnagiri — professional, dermatology-guided support for hair thinning and loss at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "advanced-skin-procedures",
    name: "Advanced Skin Procedures",
    category: "Aesthetic Dermatology",
    categorySlug: "aesthetic-dermatology",
    blurb: "Professionally supervised procedural care for skin concerns.",
    tagline: "Specialist Procedural Skin Care.",
    intro:
      "Advanced skin procedures are performed by a qualified dermatologist to address a range of skin concerns through appropriate, evaluated techniques. Each procedure is suited to your skin type, your concern and your goals, with clear guidance on safety and aftercare.",
    image: img.svcAdvancedProcedures,
    symptoms: [
      { name: "Stubborn skin concerns", desc: "Concerns not improving with topical care alone." },
      { name: "Texture and tone", desc: "Roughness, scars or uneven tone needing procedural care." },
      { name: "Cosmetic concerns", desc: "A desire for a professional, supervised treatment." },
    ],
    causes: [
      "Ageing and sun exposure",
      "Past acne or skin damage",
      "Genetics and skin type",
      "Persistent skin concerns",
    ],
    whenToSee: [
      "Topical care is not giving the result you want",
      "You are considering a procedural treatment",
      "You want safe, professional supervision",
    ],
    diagnosisSteps: [
      { title: "Consultation", desc: "Your concern and goals are discussed." },
      { title: "Skin Assessment", desc: "Your skin and the concern are examined in detail." },
      { title: "Procedure Selection", desc: "The most suitable, safe procedure is chosen." },
      { title: "Aftercare Plan", desc: "Clear aftercare and follow-up are agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Professionally supervised procedural treatments",
        "Care suited to your specific concern",
      ],
      lifestyle: [
        "Sun protection",
        "Gentle skin care",
        "Following aftercare guidance",
      ],
      followUp:
        "Depending on the procedure, follow-up sessions and reviews are planned to achieve and maintain results safely.",
    },
    myths: [
      { myth: "Procedure-based skin care is always invasive.", fact: "Many procedures are minimally invasive and performed as an outpatient treatment with limited downtime." },
      { myth: "Advanced procedures are not safe.", fact: "When performed by a qualified dermatologist and suited to your skin, they are safe and appropriate." },
    ],
    management: [
      "Consultation and skin assessment",
      "Selection of a safe, suitable procedure",
      "Clear aftercare and follow-up",
    ],
    faqs: [
      { q: "Are advanced skin procedures safe?", a: "Yes, when performed by a qualified dermatologist and suited to your skin. Your dermatologist will explain the procedure, expected results and aftercare." },
      { q: "How much downtime is involved?", a: "This varies by procedure. Many are minimally invasive with limited downtime; your dermatologist will explain what to expect." },
      { q: "Which procedure is right for me?", a: "A consultation and skin assessment help determine the most suitable approach for your concern and skin type." },
    ],
    related: ["skin-rejuvenation", "chemical-peels", "laser-hair-reduction"],
    relatedBlogs: ["why-see-a-dermatologist-in-krishnagiri"],
    seo: {
      title: "Advanced Skin Procedures in Krishnagiri | AV Clinic",
      description:
        "Advanced skin procedures in Krishnagiri — safe, professionally supervised procedural care for a range of skin concerns at AV Clinic. Book a consultation.",
    },
  },
];